/**
 * Technician Notes — shared tech knowledge about a customer / site / job
 * (parking, gate codes, safety, contacts, equipment quirks, job history —
 * any note, any category). One component for the tech Lookup tab, the
 * office Customer card, and the inline card line.
 */
import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pin, Pencil, Trash2, Plus, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { addAccessNote, editAccessNote, deleteAccessNote } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { formatNoteDate } from "@/lib/noteFormat";
import { useI18n } from "@/i18n";
import { Tx, txNote } from "@/i18n/Tx";
import { NoteText } from "@/i18n/NoteText";

export const ACCESS_CATEGORIES = [
  { key: "parking", label: "Parking", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  { key: "access", label: "Access & Codes", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "safety", label: "Safety", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  { key: "contacts", label: "Contacts", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "equipment", label: "Equipment", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { key: "history", label: "Job History", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "general", label: "General", cls: "bg-slate-100 text-slate-700 border-slate-200" },
];
const catOf = (k) => ACCESS_CATEGORIES.find(c => c.key === k) || ACCESS_CATEGORIES[ACCESS_CATEGORIES.length - 1];

export function CategoryChip({ category, size = "xs" }) {
  const { t } = useI18n();
  const c = catOf(category);
  return <span className={`inline-flex items-center rounded border px-1 font-semibold ${size === "xs" ? "text-[8px]" : "text-[10px] px-1.5"} ${c.cls}`}>{t(c.label)}</span>;
}

/** Inline line for a dispatch card: first pinned note + tap to expand all. */
export function SiteAccessLine({ notes, testId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (!notes?.length) return null;
  const first = notes[0];
  return (
    <div className="mt-0.5 rounded bg-amber-50 border border-amber-200 px-1.5 py-1 text-[8px] md:text-[10px] text-amber-900" data-testid={testId}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-1 text-left" data-testid={testId ? `${testId}-toggle` : undefined}>
        <KeyRound className="w-2.5 h-2.5 shrink-0 mt-[1px] text-amber-700" />
        <span className="flex-1 min-w-0">
          <span className="font-bold uppercase tracking-wide text-[7px] text-amber-700 mr-1">{t("Tech notes")}</span>
          {!open && <span className="break-words"><Tx {...txNote(first)} />{notes.length > 1 ? <span className="text-amber-600 font-semibold"> · +{notes.length - 1} {t("more")}</span> : null}</span>}
        </span>
        {open ? <ChevronUp className="w-2.5 h-2.5 shrink-0" /> : <ChevronDown className="w-2.5 h-2.5 shrink-0" />}
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {notes.map(n => (
            <div key={n.id} className="flex items-start gap-1">
              <CategoryChip category={n.category} />
              <span className="flex-1 break-words whitespace-pre-wrap"><Tx {...txNote(n)} /></span>
              {n.target === "client" && <span className="text-[7px] text-amber-600 shrink-0">customer</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteEditor({ initial, targets, onCancel, onSave, saving }) {
  const { t, lang } = useI18n();
  const [text, setText] = useState((initial?.text_lang === lang && initial?.text_original) || initial?.text || "");
  const [category, setCategory] = useState(initial?.category || "general");
  const [pinned, setPinned] = useState(!!initial?.pinned);
  const [target, setTarget] = useState(initial ? `${initial.target}:${initial.target_id}` : `${targets[0].target}:${targets[0].target_id}`);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2" data-testid="access-note-editor">
      <Textarea value={text} onChange={e => setText(e.target.value)} rows={3} autoFocus placeholder={t("Where to park, gate code, who to ask for, hazards…")} data-testid="access-note-text" />
      <div className="flex flex-wrap gap-1">
        {ACCESS_CATEGORIES.map(c => (
          <button key={c.key} type="button" onClick={() => setCategory(c.key)} data-testid={`access-note-cat-${c.key}`}
            className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${category === c.key ? c.cls + " ring-1 ring-slate-400" : "bg-white text-slate-500 border-slate-200"}`}>{t(c.label)}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {!initial && targets.length > 1 && (
          <select value={target} onChange={e => setTarget(e.target.value)} className="h-7 rounded border border-slate-200 bg-white px-1 text-xs" data-testid="access-note-target">
            {targets.map(t => <option key={`${t.target}:${t.target_id}`} value={`${t.target}:${t.target_id}`}>{t.label}</option>)}
          </select>
        )}
        <label className="inline-flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={pinned} onChange={e => setPinned(e.target.checked)} data-testid="access-note-pinned" /> {t("Pin to top")}</label>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="outline" className="h-7" onClick={onCancel} data-testid="access-note-cancel">{t("Cancel")}</Button>
          <Button size="sm" className="h-7 bg-slate-800 text-white hover:bg-slate-900" disabled={saving || !text.trim()} data-testid="access-note-save"
            onClick={() => { const [tg, tid] = target.split(":"); onSave({ text: text.trim(), category, pinned, target: tg, target_id: tid }); }}>
            {saving ? t("Saving…") : t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * notes: [{id,text,category,pinned,created_by,created_by_name,created_at,target,target_id,target_name}]
 * targets: [{target:"client"|"site", target_id, label}] — where a new note can be saved.
 */
export function AccessNotesPanel({ notes = [], targets = [], onChanged, compact = false, title = "Technician Notes", testId = "access-notes-panel" }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const isTech = (user?.role || "") === "technician";
  const canEdit = (n) => !isTech || (n.created_by && n.created_by === (user?.id || user?.email));

  const save = async (data, existing) => {
    setSaving(true);
    try {
      if (existing) await editAccessNote(existing.id, { target: existing.target, target_id: existing.target_id, text: data.text, category: data.category, pinned: data.pinned });
      else await addAccessNote(data);
      toast.success(existing ? t("Note updated") : t("Note saved"));
      setAdding(false); setEditing(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Could not save note")); }
    finally { setSaving(false); }
  };
  const remove = async (n) => {
    if (!window.confirm("Delete this access note?")) return;
    try { await deleteAccessNote(n.id, { target: n.target, target_id: n.target_id }); toast.success(t("Note deleted")); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Could not delete note")); }
  };

  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2">
        <KeyRound className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-amber-600`} />
        <h3 className={`${compact ? "text-xs" : "text-sm"} font-semibold text-slate-800`}>{t(title)}</h3>
        <span className="text-[10px] text-slate-400">{notes.length}</span>
        {targets.length > 0 && !adding && (
          <button type="button" onClick={() => { setAdding(true); setEditing(null); }} title={t("Add access note")} data-testid="btn-add-access-note"
            className="ml-auto inline-flex h-6 w-6 items-center justify-center rounded-md bg-amber-500 text-white hover:bg-amber-600"><Plus className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <p className="text-[10px] text-slate-400 mt-0.5">{t("Shared with every tech — parking, gate codes, who to ask for, hazards, equipment quirks, job history. Not shown to customers.")}</p>
      <div className="mt-2 space-y-1.5">
        {adding && <NoteEditor targets={targets} onCancel={() => setAdding(false)} onSave={(d) => save(d)} saving={saving} />}
        {notes.length === 0 && !adding && <div className="text-xs text-slate-400 py-2" data-testid="access-notes-empty">{t("No technician notes yet.")}</div>}
        {notes.map(n => editing?.id === n.id ? (
          <NoteEditor key={n.id} initial={n} targets={targets} onCancel={() => setEditing(null)} onSave={(d) => save(d, n)} saving={saving} />
        ) : (
          <div key={n.id} className={`rounded-lg border p-2 ${n.pinned ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`} data-testid={`access-note-${n.id}`}>
            <div className="flex items-start gap-1.5">
              {n.pinned && <Pin className="w-3 h-3 text-amber-600 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0 text-xs text-slate-800 whitespace-pre-wrap break-words"><NoteText note={n} /></div>
              {canEdit(n) && (
                <div className="flex gap-0.5 shrink-0">
                  <button type="button" onClick={() => { setEditing(n); setAdding(false); }} className="p-1 text-slate-400 hover:text-blue-600" title={t("Edit")} data-testid={`access-note-edit-${n.id}`}><Pencil className="w-3 h-3" /></button>
                  <button type="button" onClick={() => remove(n)} className="p-1 text-slate-400 hover:text-rose-600" title={t("Delete")} data-testid={`access-note-delete-${n.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] text-slate-400">
              <CategoryChip category={n.category} size="sm" />
              {n.target_name && <span className="inline-flex items-center gap-0.5">{n.target === "site" ? "Site" : "Customer"}: <b className="text-slate-500">{n.target_name}</b></span>}
              <span className="ml-auto">{n.created_by_name || "—"} · {formatNoteDate(n.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AccessNotesSheet({ open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] overflow-y-auto p-4 relative" onClick={e => e.stopPropagation()}>
        <button type="button" onClick={onClose} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600" data-testid="access-notes-sheet-close"><X className="w-4 h-4" /></button>
        {children}
      </div>
    </div>
  );
}
